import { ACWResult } from '../../components/acw/acw.types';
import { activityApi } from '../../services/api';
import { saveDisposition } from '../../services/cxone-sdk.service';
import { isInvalidContactIdError } from '../../services/cxone-sdk/transfer/disposition-error.util';
import {
  SapContact,
  generateCallReferenceId,
  openSapContactViaCTI,
  sendSapEnd,
  sendSapOutboundEnd,
} from '../../services/sap-cti.service';
import {
  clearCallTrace,
  getCallTraceId,
  logCallActivity,
} from '../../services/call-log/call-log.service';
import { runExclusive } from '../../services/call-log/cross-tab-call-guard.util';
import { toCallLogContactSummaries } from '../../services/call-log/call-log-contact.util';
import { SAPContact } from '../../types';
import { logger } from '../../utils/logger';
import { findSapContactByIdentifier } from '../../services/sap-cti/sap-contact-identity.util';
import { LastCallInfo, OutboundCallInfo } from './types';

const SAP_CONTACT_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isSapContactUuid(value: string | undefined | null): value is string {
  return typeof value === 'string' && SAP_CONTACT_UUID.test(value);
}

function sapContactDisplayName(contact: SAPContact): string {
  if (contact.type === 'corporate') {
    return contact.accountName?.trim() || 'Unknown Contact';
  }
  return `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'Unknown Contact';
}

interface ResolvedAcwSapContact {
  contact: SapContact;
  /** True when ACCEPT was never sent during the call — caller should send it before END. */
  needsAccept: boolean;
}

/**
 * Resolve the ACW-selected SAP contact for CTI END.
 * Prefer an already-opened contact (has referenceId from ACCEPT).
 * If ACW selected a search match that was never opened, synthesize a referenceId
 * so we can still ACCEPT + create activity + END.
 */
function resolveSelectedSapContact(
  sapContactId: string | undefined,
  openedSapContacts: SapContact[],
  displayedContacts: SAPContact[],
  fallbackPhone: string | undefined,
  cxoneContactId?: string,
  preferredReferenceId?: string
): ResolvedAcwSapContact | null {
  if (!sapContactId) {
    return null;
  }

  const opened = findSapContactByIdentifier(openedSapContacts, sapContactId);
  if (opened?.referenceId) {
    return { contact: opened, needsAccept: false };
  }

  const matched = findSapContactByIdentifier(displayedContacts, sapContactId);
  if (matched) {
    const phone = matched.phone || opened?.phone || fallbackPhone;
    if (!phone) {
      return null;
    }

    return {
      contact: {
        id: matched.id,
        displayId: matched.displayId || opened?.displayId,
        firstName: matched.firstName || opened?.firstName,
        name: sapContactDisplayName(matched),
        phone,
        referenceId:
          opened?.referenceId || preferredReferenceId || generateCallReferenceId(cxoneContactId),
        type: matched.type,
        accountId: matched.accountId || opened?.accountId,
        startedAt: opened?.startedAt,
      },
      needsAccept: !opened?.referenceId,
    };
  }

  // Opened without referenceId (should be rare) — mint one so END can still fire.
  if (opened) {
    const phone = opened.phone || fallbackPhone;
    if (!phone) {
      return null;
    }
    return {
      contact: {
        ...opened,
        phone,
        referenceId: preferredReferenceId || generateCallReferenceId(cxoneContactId),
      },
      needsAccept: true,
    };
  }

  return null;
}

async function saveCXoneDispositionExclusive(
  contactId: string,
  dispositionId: string,
  notes: string | undefined
): Promise<void> {
  const outcome = await runExclusive(`disposition:${contactId}`, async () => {
    logger.debug('[Call] Saving CXone disposition', { contactId, dispositionId });
    await saveDisposition(contactId, dispositionId, notes);
  });

  if (outcome === 'skipped') {
    logger.lifecycle('[Call] Disposition already claimed by another Agent Desktop tab', {
      contactId,
    });
  }
}

interface CompleteAcwParams {
  result: ACWResult;
  lastCallInfo: LastCallInfo | null;
  acwContactId?: string;
  requireDisposition?: boolean;
  lastCallerPhone: string | null;
  openedSapContacts: SapContact[];
  displayedContacts: SAPContact[];
  isOutboundCall: boolean;
  outboundCallInfo: OutboundCallInfo | null;
}

export async function completeAcwSession({
  result,
  lastCallInfo,
  acwContactId,
  requireDisposition = false,
  lastCallerPhone,
  openedSapContacts,
  displayedContacts,
  isOutboundCall,
  outboundCallInfo,
}: CompleteAcwParams): Promise<void> {
  const contactId = acwContactId || lastCallInfo?.contactId;

  if (result.dispositionId) {
    if (!contactId) {
      throw new Error('Missing CXone contact ID — cannot save disposition');
    }

    try {
      await saveCXoneDispositionExclusive(contactId, result.dispositionId, result.notes);
    } catch (err) {
      // Contact already closed / dispositioned — retrying cannot recover. Continue so SAP
      // activity + CTI END still run and the agent is not stuck in ACW.
      if (isInvalidContactIdError(err)) {
        logger.warn(
          '[Call] CXone disposition skipped — InvalidContactId (contact already closed)',
          {
            contactId,
            dispositionId: result.dispositionId,
            requireDisposition,
          }
        );
      } else {
        logger.error('[Call] Disposition submission failed', {
          contactId,
          dispositionId: result.dispositionId,
          err,
        });
        throw err instanceof Error
          ? err
          : new Error(`Disposition submission failed (contactId=${contactId})`);
      }
    }
  } else if (requireDisposition) {
    throw new Error('Disposition is required before going Available');
  }

  const phoneNumber =
    lastCallerPhone ||
    outboundCallInfo?.phone ||
    openedSapContacts.find((contact) => contact.phone)?.phone ||
    displayedContacts.find((contact) => contact.phone)?.phone;

  const requestedSapContactId =
    result.sapContactId || (isOutboundCall ? outboundCallInfo?.sapContactId : undefined);
  const resolved = resolveSelectedSapContact(
    requestedSapContactId,
    openedSapContacts,
    displayedContacts,
    phoneNumber || undefined,
    contactId,
    isOutboundCall ? outboundCallInfo?.referenceId : undefined
  );
  const selectedContact = resolved?.contact ?? null;

  const direction = isOutboundCall ? 'OUTBOUND' : 'INBOUND';
  let sapActivityId: string | undefined;

  const endPayload = {
    dispositionCode: result.dispositionId || 'NO_DISPOSITION',
    dispositionName: result.dispositionName || 'No Disposition',
    notes: result.notes,
  };

  if (selectedContact?.referenceId && phoneNumber) {
    try {
      // Matched in search but never linked during the call — ACCEPT first so END correlates.
      if (resolved?.needsAccept) {
        openSapContactViaCTI(
          selectedContact.id,
          selectedContact.displayId,
          selectedContact.referenceId,
          phoneNumber,
          selectedContact.type,
          direction,
          selectedContact.firstName
        );
      }

      const activityResponse = await activityApi.createActivity({
        phone: phoneNumber,
        contactId: selectedContact.id,
        contactType: selectedContact.type || 'individual',
        accountId: selectedContact.accountId,
        referenceId: selectedContact.referenceId,
        direction,
        startDateTime: selectedContact.startedAt || outboundCallInfo?.startedAt,
        disposition: {
          code: result.dispositionId || 'NO_DISPOSITION',
          name: result.dispositionName || 'No Disposition',
          notes: result.notes,
        },
      });

      sapActivityId = activityResponse.data.phoneCallId;

      if (isOutboundCall) {
        sendSapOutboundEnd(
          phoneNumber,
          selectedContact.id,
          selectedContact.referenceId,
          endPayload,
          selectedContact.displayId
        );
      } else {
        sendSapEnd(
          phoneNumber,
          selectedContact.id,
          selectedContact.referenceId,
          endPayload,
          selectedContact.displayId
        );
      }
    } catch (activityErr) {
      logger.error('[Widget] Failed to create SAP activity:', activityErr);
    }
  } else if (
    isOutboundCall &&
    outboundCallInfo &&
    phoneNumber &&
    isSapContactUuid(outboundCallInfo.sapContactId)
  ) {
    try {
      const activityResponse = await activityApi.createActivity({
        phone: phoneNumber,
        contactId: outboundCallInfo.sapContactId,
        contactType: 'individual',
        referenceId: outboundCallInfo.referenceId,
        direction: 'OUTBOUND',
        startDateTime: outboundCallInfo.startedAt,
        disposition: {
          code: result.dispositionId || 'NO_DISPOSITION',
          name: result.dispositionName || 'No Disposition',
          notes: result.notes,
        },
      });
      sapActivityId = activityResponse.data.phoneCallId;

      // Keep CTI END in sync with the REST activity created for click-to-dial.
      sendSapOutboundEnd(
        phoneNumber,
        outboundCallInfo.sapContactId,
        outboundCallInfo.referenceId,
        endPayload
      );
    } catch (activityErr) {
      logger.error('[Call] Failed to create outbound SAP activity', activityErr);
    }
  } else if (isOutboundCall && outboundCallInfo && phoneNumber) {
    logger.debug('[Call] Skipping outbound SAP activity — no valid SAP contact UUID');
  }

  logCallActivity(
    'DISPOSITION_COMPLETED',
    {
      direction,
      phone: phoneNumber,
      cxoneContactId: contactId ?? lastCallInfo?.contactId,
      skillId: lastCallInfo?.skillId,
      skillName: lastCallInfo?.skillName,
      disposition: {
        id: result.dispositionId,
        name: result.dispositionName,
        notes: result.notes,
      },
      displayedContacts: toCallLogContactSummaries(displayedContacts),
      openedContacts: openedSapContacts,
      selectedContact: selectedContact
        ? {
            id: selectedContact.id,
            name: selectedContact.name,
            phone: selectedContact.phone,
            type: selectedContact.type,
            accountId: selectedContact.accountId,
          }
        : null,
      selectedSapContactId:
        result.sapContactId || selectedContact?.id || outboundCallInfo?.sapContactId,
      sapActivityId,
      callTraceId: getCallTraceId(),
    },
    contactId ?? lastCallInfo?.contactId
  );
  clearCallTrace(contactId ?? lastCallInfo?.contactId);
}
